import { apiFetch } from "@/integrations/backend/client";

export type ClientAiWorkoutJobStep =
  | "gathering"
  | "planning"
  | "matching"
  | "refining"
  | "saving"
  // Legacy steps from the nearby-pool pipeline
  | "selecting"
  | "prescribing";

export type ClientAiWorkoutJobStatus =
  | "queued"
  | "running"
  | "awaiting_review"
  | "complete"
  | "failed"
  | "cancelled";

export type ClientAiWorkoutDraftExercise = {
  exercise_id: string | null;
  exercise_name: string;
  sets?: number | null;
  reps?: number | null;
  time_seconds?: number | null;
  rest_seconds?: number | null;
  weight_kg?: number | null;
  weight_lbs?: number | null;
  difficulty?: string | null;
};

export type ClientAiWorkoutDraftGrouping = {
  sets?: number | null;
  rest_seconds?: number | null;
  exercises: ClientAiWorkoutDraftExercise[];
};

export type ClientAiWorkoutDraft = {
  name: string;
  date: string | null;
  description: string;
  duration_minutes?: number | null;
  groupings: ClientAiWorkoutDraftGrouping[];
};

export type ClientAiWorkoutJob = {
  job_id: string;
  status: ClientAiWorkoutJobStatus;
  step: ClientAiWorkoutJobStep;
  assigned_workout_id: string | null;
  name: string | null;
  error: string;
  target_date: string | null;
  draft: ClientAiWorkoutDraft | null;
};

async function requestJson<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: string };
      detail = (data.detail || "").trim();
    } catch {
      // ignore
    }
    throw new Error(detail || `${path}_failed:${res.status}`);
  }
  return (await res.json()) as T;
}

export async function startClientAiWorkoutGeneration(input: {
  prompt?: string;
  date: string;
}): Promise<ClientAiWorkoutJob> {
  return requestJson<ClientAiWorkoutJob>("/api/v1/workouts/ai-generate", "POST", {
    prompt: input.prompt ?? "",
    date: input.date,
  });
}

export async function getClientAiWorkoutJob(jobId: string): Promise<ClientAiWorkoutJob> {
  return requestJson<ClientAiWorkoutJob>(
    `/api/v1/workouts/ai-generate/${encodeURIComponent(jobId)}`,
    "GET"
  );
}

export async function cancelClientAiWorkoutGeneration(jobId: string): Promise<ClientAiWorkoutJob | null> {
  try {
    return await requestJson<ClientAiWorkoutJob>(
      `/api/v1/workouts/ai-generate/${encodeURIComponent(jobId)}/cancel`,
      "POST",
      {}
    );
  } catch {
    // Best-effort on leave/unmount — ignore network/auth failures.
    return null;
  }
}

export async function confirmClientAiWorkoutGeneration(jobId: string): Promise<ClientAiWorkoutJob> {
  return requestJson<ClientAiWorkoutJob>(
    `/api/v1/workouts/ai-generate/${encodeURIComponent(jobId)}/confirm`,
    "POST",
    {}
  );
}

export const CLIENT_AI_WORKOUT_STEP_LABELS: Record<ClientAiWorkoutJobStep, string> = {
  gathering: "Looking at your recent training…",
  planning: "Designing your session…",
  matching: "Finding the closest exercises in our library…",
  refining: "Dialing in sets, reps, and rest…",
  saving: "Getting your session ready…",
  selecting: "Picking the right moves…",
  prescribing: "Dialing in sets and rest…",
};

export const CLIENT_AI_WORKOUT_STEP_PROGRESS: Record<ClientAiWorkoutJobStep, number> = {
  gathering: 12,
  planning: 35,
  matching: 60,
  refining: 85,
  saving: 95,
  selecting: 35,
  prescribing: 60,
};

export async function waitForClientAiWorkoutJob(
  jobId: string,
  onUpdate?: (job: ClientAiWorkoutJob) => void,
  shouldStop?: () => boolean
): Promise<ClientAiWorkoutJob> {
  const timeoutMs = 600_000;
  const intervalMs = 1_500;
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    if (shouldStop?.()) {
      throw new DOMException("Generation cancelled", "AbortError");
    }
    const job = await getClientAiWorkoutJob(jobId);
    onUpdate?.(job);
    if (
      job.status === "awaiting_review" ||
      job.status === "complete" ||
      job.status === "failed" ||
      job.status === "cancelled"
    ) {
      return job;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error("Workout generation timed out. Please try again.");
}
